import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import type { Nullable } from '../types/common';
import type { CapacityValues, DateTimeFilter } from '../types/components/filter_bar_types';

const setItemWithExpiry = (key: string, value: string, ttl: number) => {
  localStorage.setItem(key, value);
  localStorage.setItem(
    key + '_expiry',
    (new Date().getTime() + ttl).toString(),
  );
};

const getItemWithExpiry = (key: string) => {
  const itemStr = localStorage.getItem(key);
  const expiryStr = localStorage.getItem(key + '_expiry');
  if (!itemStr || !expiryStr) return null;

  if (new Date().getTime() > parseInt(expiryStr)) {
    localStorage.removeItem(key);
    return null;
  }
  return itemStr;
};
interface AvailabilityContextType {
  dateTimeFilter: DateTimeFilter | null;
  setDateTimeFilter: (filter: DateTimeFilter | null) => void;
  
  startDate: Nullable<Dayjs>;
  startOffset: Nullable<number>;
  endOffset: Nullable<number>;
  setStartDate: (date: Nullable<Dayjs>) => void;
  setStartOffset: (offset: Nullable<number>) => void;
  setEndOffset: (offset: Nullable<number>) => void;
  
  origin: Nullable<string>;
  setOrigin: (origin: Nullable<string>) => void;
  capacityFilterValues: CapacityValues;
  setCapacityFilterValues: (capacityFilterValues: CapacityValues) => void;
}

const AvailabilityContext = createContext<AvailabilityContextType | undefined>(
  undefined,
);

export const AvailabilityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize from localStorage
  const initialStartDate = getItemWithExpiry('startDate')
    ? dayjs(getItemWithExpiry('startDate'))
    : null;
  const initialStartOffset = getItemWithExpiry('startOffset')
    ? parseInt(getItemWithExpiry('startOffset') || '0')
    : null;
  const initialEndOffset = getItemWithExpiry('endOffset')
    ? parseInt(getItemWithExpiry('endOffset') || '0')
    : null;

  const [startDate, setStartDate] = useState<Nullable<Dayjs>>(initialStartDate);
  const [startOffset, setStartOffset] = useState<Nullable<number>>(initialStartOffset);
  const [endOffset, setEndOffset] = useState<Nullable<number>>(initialEndOffset);

  const dateTimeFilter = useMemo(() => ({
    date: startDate,
    startOffset: startOffset,
    endOffset: endOffset,
  }), [startDate, startOffset, endOffset]);

  const setDateTimeFilter = useCallback((filter: DateTimeFilter | null) => {
    setStartDate(filter?.date || null);
    setStartOffset(filter?.startOffset || null);
    setEndOffset(filter?.endOffset || null);
  }, [setStartDate, setStartOffset, setEndOffset]);

  const [origin, setOrigin] = useState<Nullable<string>>(
    getItemWithExpiry('origin') || 'marketplace',
  );

  const [capacityFilterValues, setCapacityFilterValues] =
    useState<CapacityValues>({
      minGuests: undefined,
      maxGuests: undefined,
      allowSeated: false,
      allowStanding: false,
    });

  useEffect(() => {
    const saveFor = 1000 * 60 * 60; // 1 hour
    setItemWithExpiry('startDate', startDate?.toISOString() || '', saveFor);
    setItemWithExpiry('startOffset', startOffset?.toString() || '', saveFor);
    setItemWithExpiry('endOffset', endOffset?.toString() || '', saveFor);
    setItemWithExpiry('origin', origin || '', saveFor);
  }, [startDate, startOffset, endOffset, origin]);
  return (
    <AvailabilityContext.Provider
      value={{
        dateTimeFilter,
        setDateTimeFilter,
        startDate,
        startOffset,
        endOffset,
        setStartDate,
        setStartOffset,
        setEndOffset,
        origin,
        setOrigin,
        capacityFilterValues,
        setCapacityFilterValues,
      }}
    >
      {children}
    </AvailabilityContext.Provider>
  );
};

export const useAvailability = () => {
  const context = useContext(AvailabilityContext);
  if (context === undefined) {
    throw new Error(
      'useAvailability must be used within an AvailabilityProvider',
    );
  }
  return context;
};
